import type { SuscripcionEstado } from '@/modelos/Auth';
import type { PlanId, PlanStatusCardProps } from '@/contasis/layout/UserMenu/PlanStatusCard.types';
import { ensureBusinessDateIso, getBusinessTodayISODate } from '@/shared/time/businessTime';
import { formatLongDateLabelFromString as formatShortLabel } from '@/utils/formatters';

const PLAN_ID_POR_NOMBRE: Record<string, PlanId> = {
  Emprende: 'emprende',
  Despega: 'despega',
  Maximiza: 'maximiza',
  Ilimitado: 'maximiza',
};

const DIAS_UMBRAL_ULTIMOS_DIAS = 3;
const DIAS_UMBRAL_PAGADO_POR_VENCER = 7;
const DIAS_UMBRAL_FACTURACION_ANUAL = 200;

function diferenciaEnDias(desdeIso: string, hastaIso: string): number {
  const [y1, m1, d1] = desdeIso.split('-').map(Number);
  const [y2, m2, d2] = hastaIso.split('-').map(Number);
  const ms = Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1);
  return Math.round(ms / 86_400_000);
}

interface HandlersPlanStatusCard {
  onVerPlanes: () => void;
  onGestionar: () => void;
  /** Se usa cuando la empresa activa es la demo "Senciyo SAC" (estado 'SinCompletarOnboarding') — lleva a crear la empresa real */
  onCrearEmpresa: () => void;
}

export function construirPlanStatusCard(
  suscripcion: SuscripcionEstado | null,
  handlers: HandlersPlanStatusCard,
): PlanStatusCardProps | undefined {
  if (!suscripcion) return undefined;

  if (suscripcion.estado === 'SinCompletarOnboarding') {
    return {
      plan: 'demo',
      planNombre: 'Senciyo SAC',
      estado: 'demoPrivada',
      onAccion: handlers.onCrearEmpresa,
    };
  }

  const plan = suscripcion.plan ? (PLAN_ID_POR_NOMBRE[suscripcion.plan] ?? 'emprende') : 'emprende';
  const planNombre = suscripcion.nombrePlan?.trim() || plan.charAt(0).toUpperCase() + plan.slice(1);
  const { onVerPlanes, onGestionar } = handlers;

  // La etiqueta "Prueba gratis" depende solo de endsAt/trialEndsAt, nunca de estado:
  // un plan ya pagado que está "PagoEnValidacion" también puede no tener endsAt aún,
  // pero en cuanto endsAt exista deja de pasar por esta rama.
  if (!suscripcion.endsAt && suscripcion.trialEndsAt) {
    const hoy = getBusinessTodayISODate();
    const finVigenciaIso = ensureBusinessDateIso(suscripcion.trialEndsAt);
    const diasRestantes = Math.max(0, diferenciaEnDias(hoy, finVigenciaIso));

    let progreso: number | undefined;
    if (suscripcion.startsAt) {
      const startsAtIso = ensureBusinessDateIso(suscripcion.startsAt);
      const totalDias = diferenciaEnDias(startsAtIso, finVigenciaIso);
      if (totalDias > 0) {
        const diasTranscurridos = diferenciaEnDias(startsAtIso, hoy);
        progreso = Math.min(100, Math.max(0, Math.round((diasTranscurridos / totalDias) * 100)));
      }
    }

    if (!suscripcion.activo || diasRestantes <= 0) {
      return {
        plan,
        planNombre,
        estado: 'vencido',
        diasRestantes,
        etiquetaAccion: 'Comprar plan',
        onAccion: onVerPlanes,
      };
    }

    if (diasRestantes <= DIAS_UMBRAL_ULTIMOS_DIAS) {
      return {
        plan,
        planNombre,
        estado: 'ultimosDias',
        diasRestantes,
        progreso,
        fechaVencimiento: formatShortLabel(suscripcion.trialEndsAt),
        etiquetaAccion: 'Actualizar plan',
        onAccion: onVerPlanes,
      };
    }

    return {
      plan,
      planNombre,
      estado: 'prueba',
      diasRestantes,
      progreso,
      etiquetaAccion: 'Actualizar plan',
      onAccion: onVerPlanes,
    };
  }

  if (!suscripcion.endsAt) {
    return {
      plan,
      planNombre,
      estado: 'vencido',
      diasRestantes: 0,
      etiquetaAccion: 'Comprar plan',
      onAccion: onVerPlanes,
    };
  }

  // Con endsAt presente, la etiqueta se decide únicamente por el estado que manda el backend.
  const hoy = getBusinessTodayISODate();
  const finVigenciaIso = ensureBusinessDateIso(suscripcion.endsAt);
  const diasRestantes = Math.max(0, diferenciaEnDias(hoy, finVigenciaIso));
  const fechaRenovacion = formatShortLabel(suscripcion.endsAt);

  switch (suscripcion.estado) {
    case 'PagoEnValidacion':
      return {
        plan,
        planNombre,
        estado: 'enValidacion',
        etiquetaAccion: 'Ver estado',
        onAccion: onGestionar,
      };

    case 'CancelacionProgramada':
      return {
        plan,
        planNombre,
        estado: 'cancelacionProgramada',
        fechaRenovacion,
        etiquetaAccion: 'Reactivar',
        onAccion: onGestionar,
      };

    case 'SoloConsulta':
      return {
        plan,
        planNombre,
        estado: 'soloConsulta',
        etiquetaAccion: 'Actualizar plan',
        onAccion: onVerPlanes,
      };

    case 'Vencido':
      return {
        plan,
        planNombre,
        estado: 'pagadoVencido',
        fechaRenovacion,
        etiquetaAccion: 'Renovar',
        onAccion: onGestionar,
      };

    case 'Cancelado':
      return {
        plan,
        planNombre,
        estado: 'cancelado',
        fechaRenovacion,
        etiquetaAccion: 'Elegir plan',
        onAccion: onVerPlanes,
      };

    case 'Eliminado':
      return {
        plan,
        planNombre,
        estado: 'eliminado',
        etiquetaAccion: 'Contactar soporte',
        onAccion: onGestionar,
      };

    case 'Pagado':
    default: {
      // Pagado y endsAt presente, pero puede estar por vencer. El sistema de cobros aún no
      // nos dice si la renovación automática está prendida o apagada — se asume que sí
      // (renovacionAutomatica queda en su default) hasta que ese dato llegue del backend.
      const facturacion =
        suscripcion.startsAt &&
        diferenciaEnDias(ensureBusinessDateIso(suscripcion.startsAt), finVigenciaIso) > DIAS_UMBRAL_FACTURACION_ANUAL
          ? 'anual'
          : 'mensual';

      if (diasRestantes <= DIAS_UMBRAL_PAGADO_POR_VENCER) {
        return {
          plan,
          planNombre,
          estado: 'pagadoPorVencer',
          diasRestantes,
          fechaVencimiento: fechaRenovacion,
          etiquetaAccion: 'Renovar',
          onAccion: onGestionar,
        };
      }

      return {
        plan,
        planNombre,
        estado: 'pagado',
        facturacion,
        fechaRenovacion,
        etiquetaAccion: 'Mejorar plan',
        onAccion: onGestionar,
      };
    }
  }
}
